import { useQuery } from '@tanstack/react-query'
import { CalendarClock, Layers, Lock, Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { classificationSchemesQueryOptions } from '@/api/loans-api'
import type { AgeingRun, ClassificationRun } from '@/api/loans-types'
import { ErrorSurface, ProblemAlert } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatBusinessDate, formatShortDate } from '@/lib/datetime'
import type { ApiError } from '@/lib/problem-details'

import { useBranchId, useBusinessDate } from './use-business-day'
import { PROVISION_BASIS_LABELS, formatRate } from './constants'
import { LOAN_PERMISSIONS, useLoanPermissions } from './permissions'
import { LoanLink } from './loan-link'
import { loanPaths } from './loan-paths'
import { DateField } from './operation-form'
import { EmptyPanel, Field, Figure, Section } from './panel-primitives'

/**
 * The two portfolio runs, and the schemes they are run against.
 *
 * Both are branch-wide and both are ordinarily nightly jobs; this is the screen that triggers one
 * by hand, which is why each panel says plainly what a run does before it offers the button.
 *
 * Their permissions do not begin with `loans.`, and that is not an oversight. Ageing is the
 * operations module's job (`ops.jobs.trigger`); classification posts a provision movement into
 * the ledger (`ledger.provision.run`). The server checks those codes, so these panels do too.
 */

function RunPanel({
  title,
  description,
  permission,
  permitted,
  asOf,
  onAsOfChange,
  dateFieldId,
  submitLabel,
  isPending,
  error,
  onRun,
  branchId,
  children,
}: {
  title: string
  description: string
  permission: string
  permitted: boolean
  asOf: string
  onAsOfChange: (value: string) => void
  dateFieldId: string
  submitLabel: string
  isPending: boolean
  error: ApiError | null
  onRun: () => void
  branchId: string | null
  children?: ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          <p className="mt-0.5 text-xs text-muted">{description}</p>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!permitted ? (
          <Alert tone="info">
            <Lock aria-hidden />
            <div className="space-y-1">
              <AlertTitle>Your role cannot trigger this</AlertTitle>
              <AlertDescription>
                It needs <span className="font-mono text-xs">{permission}</span>. The run happens on
                its own schedule regardless; this screen only brings it forward.
              </AlertDescription>
            </div>
          </Alert>
        ) : branchId === null ? (
          <Alert tone="warning">
            <AlertTitle>No branch is selected</AlertTitle>
            <AlertDescription>
              A run acts on one branch. Choose one in the header before triggering it.
            </AlertDescription>
          </Alert>
        ) : (
          <DateField
            id={dateFieldId}
            label="As at"
            value={asOf}
            onChange={onAsOfChange}
            hint="The branch's business date, not today's. A run is safe to repeat for the same date."
          />
        )}

        {error ? <ProblemAlert error={error} /> : null}
        {children}
      </CardContent>

      {permitted && branchId !== null ? (
        <CardFooter>
          <Button variant="primary" disabled={isPending || asOf === ''} onClick={onRun}>
            {isPending ? 'Running…' : submitLabel}
          </Button>
        </CardFooter>
      ) : null}
    </Card>
  )
}

export function AgeingRunPanel({
  run,
  isPending,
  error,
  onRun,
}: {
  run: AgeingRun | undefined
  isPending: boolean
  error: ApiError | null
  onRun: (asOf: string, branchId: string) => void
}) {
  const permissions = useLoanPermissions()
  const branchId = useBranchId()
  const businessDay = useBusinessDate()
  const [asOf, setAsOf] = useState('')

  const effectiveAsOf = asOf === '' ? (businessDay.date ?? '') : asOf

  return (
    <RunPanel
      title="Age the portfolio"
      description="Walks the branch's live rounds, moves what is overdue into arrears and accrues penalty."
      permission={LOAN_PERMISSIONS.ageingRun}
      permitted={permissions.canRunAgeing}
      asOf={effectiveAsOf}
      onAsOfChange={setAsOf}
      dateFieldId="ageing-as-of"
      submitLabel="Run ageing"
      isPending={isPending}
      error={error}
      branchId={branchId}
      onRun={() => {
        if (branchId && effectiveAsOf !== '') {
          onRun(effectiveAsOf, branchId)
        }
      }}
    >
      {run ? (
        <div className="grid gap-4 rounded-card border border-border-subtle bg-surface-muted p-4 sm:grid-cols-4">
          <Figure label="As at">
            <p className="text-sm text-heading">{formatBusinessDate(run.asOf)}</p>
          </Figure>
          <Figure label="Rounds aged">
            <p className="numeric text-sm text-heading tabular-nums">{run.cyclesAged}</p>
          </Figure>
          <Figure label="Now in arrears">
            <p className="numeric text-sm text-heading tabular-nums">{run.cyclesInArrears}</p>
          </Figure>
          <Figure label="Penalty charged" emphasis>
            <MoneyDisplay
              value={run.penaltyChargedMinor}
              currency={run.currencyCode}
              emphasis="strong"
              className="text-left"
            />
          </Figure>
        </div>
      ) : null}
    </RunPanel>
  )
}

export function ClassificationRunPanel({
  run,
  isPending,
  error,
  onRun,
}: {
  run: ClassificationRun | undefined
  isPending: boolean
  error: ApiError | null
  onRun: (asOf: string, branchId: string) => void
}) {
  const permissions = useLoanPermissions()
  const branchId = useBranchId()
  const businessDay = useBusinessDate()
  const [asOf, setAsOf] = useState('')

  const effectiveAsOf = asOf === '' ? (businessDay.date ?? '') : asOf

  return (
    <RunPanel
      title="Classify and provision"
      description="Puts every round in its band and posts the movement in the provision to the ledger."
      permission={LOAN_PERMISSIONS.provisionRun}
      permitted={permissions.canRunClassification}
      asOf={effectiveAsOf}
      onAsOfChange={setAsOf}
      dateFieldId="classification-as-of"
      submitLabel="Run classification"
      isPending={isPending}
      error={error}
      branchId={branchId}
      onRun={() => {
        if (branchId && effectiveAsOf !== '') {
          onRun(effectiveAsOf, branchId)
        }
      }}
    >
      {run ? (
        <div className="space-y-3">
          {!run.schemeConfigured ? (
            <Alert tone="warning">
              <AlertTitle>No scheme was in force</AlertTitle>
              <AlertDescription>
                Nothing was classified and nothing was provisioned. A scheme has to be put in force
                before this run means anything.
              </AlertDescription>
            </Alert>
          ) : run.schemeIsProvisional ? (
            <Alert tone="warning">
              <AlertTitle>Run against a provisional scheme</AlertTitle>
              <AlertDescription>
                The scheme in force has not been reconciled against a confirmed directive. The
                figures below are usable internally; treat them with care in anything reported
                outward.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 rounded-card border border-border-subtle bg-surface-muted p-4 sm:grid-cols-3 lg:grid-cols-5">
            <Figure label="As at">
              <p className="text-sm text-heading">{formatBusinessDate(run.asOf)}</p>
            </Figure>
            <Figure label="Classified">
              <p className="numeric text-sm text-heading tabular-nums">{run.classified}</p>
            </Figure>
            <Figure label="Unclassified">
              <div>
                <p className="numeric text-sm text-heading tabular-nums">{run.unclassified}</p>
                {run.unclassified > 0 ? (
                  <p className="text-2xs text-muted">
                    No band matched these. They are not performing — they are unclassified.
                  </p>
                ) : null}
              </div>
            </Figure>
            <Figure label="Provision required" emphasis>
              <MoneyDisplay
                value={run.provisionRequiredMinor}
                currency={run.currencyCode}
                emphasis="strong"
                className="text-left"
              />
            </Figure>
            <Figure label="Movement posted" emphasis>
              <div className="space-y-0.5">
                <MoneyDisplay
                  value={run.provisionMovementMinor}
                  currency={run.currencyCode}
                  emphasis="strong"
                  signed
                  className="text-left"
                />
                <p className="text-2xs text-muted">
                  {run.journalEntry ? (
                    <>
                      Journal <span className="font-mono">{run.journalEntry}</span>
                    </>
                  ) : (
                    'No journal — there was no movement to post.'
                  )}
                </p>
              </div>
            </Figure>
          </div>
        </div>
      ) : null}
    </RunPanel>
  )
}

/** The schemes on file, newest effective date first. A scheme is superseded, never edited. */
export function ClassificationSchemesPanel() {
  const permissions = useLoanPermissions()
  const query = useQuery(classificationSchemesQueryOptions())

  if (query.isPending) {
    return <Skeleton className="h-48 w-full" />
  }

  if (query.isError) {
    return (
      <ErrorSurface
        error={query.error}
        onRetry={() => void query.refetch()}
        title="The classification schemes could not be read"
      />
    )
  }

  const schemes = [...query.data].sort((left, right) =>
    right.effectiveFrom.localeCompare(left.effectiveFrom),
  )

  const action = permissions.canConfigureClassification ? (
    <Button asChild variant="secondary" size="sm">
      <LoanLink to={loanPaths.newClassificationScheme}>
        <Plus />
        New scheme
      </LoanLink>
    </Button>
  ) : null

  if (schemes.length === 0) {
    return (
      <EmptyPanel
        icon={<Layers className="size-6" />}
        title="No classification scheme is in force"
        description="Until one is, a classification run classifies nothing and provisions nothing — and every round reads as unclassified rather than as performing."
        action={action}
      />
    )
  }

  return (
    <Section
      title="Classification schemes"
      description="Newest effective date first. A scheme is superseded by a later one, never edited."
      actions={action}
    >
      <div className="space-y-4">
        {schemes.map((scheme) => (
          <div key={scheme.id} className="space-y-3 rounded-control border border-border-subtle p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-heading">
                {scheme.name}{' '}
                <span className="font-mono text-xs text-muted">{scheme.code}</span>
              </p>
              <p className="flex items-center gap-1.5 text-xs text-muted">
                <CalendarClock className="size-3.5" aria-hidden />
                In force from {formatShortDate(scheme.effectiveFrom)}
              </p>
            </div>

            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-4">
              <Field
                label="Provision basis"
                value={PROVISION_BASIS_LABELS[scheme.provisionBasis]}
              />
              <Field
                label="Status"
                value={scheme.isProvisional ? 'Provisional' : 'Confirmed'}
                hint={
                  scheme.isProvisional
                    ? 'Not yet reconciled against a confirmed directive.'
                    : undefined
                }
              />
              <Field label="Source" value={scheme.sourceReference} />
              <Field
                label="Restructured floor"
                value={
                  scheme.restructuredFloorClassCode === null
                    ? null
                    : `${scheme.restructuredFloorClassCode}${
                        scheme.restructuredCoolingDays === null
                          ? ''
                          : ` for ${scheme.restructuredCoolingDays} days`
                      }`
                }
                hint={
                  scheme.restructuredFloorClassCode === null
                    ? 'No floor imposed on a restructured facility.'
                    : undefined
                }
              />
            </dl>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Class</TableHead>
                  <TableHead>Days overdue</TableHead>
                  <TableHead className="text-right">Provision</TableHead>
                  <TableHead>Performing</TableHead>
                  <TableHead>Interest</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scheme.bands.map((band) => (
                  <TableRow key={band.classCode}>
                    <TableCell>
                      <span className="font-medium text-heading">{band.classCode}</span>{' '}
                      <span className="text-xs text-muted">{band.className}</span>
                    </TableCell>
                    <TableCell className="numeric tabular-nums">
                      {band.minDaysOverdue}
                      {band.maxDaysOverdue === null ? '+' : `–${band.maxDaysOverdue}`}
                    </TableCell>
                    <TableCell className="numeric text-right tabular-nums">
                      {formatRate(band.provisionRate, 0)}
                    </TableCell>
                    <TableCell>{band.isPerforming ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {band.suspendsInterestAccrual ? 'Accrual suspended' : 'Accrues'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>
    </Section>
  )
}
